import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"
import { hideIntercom, showIntercom } from "../helpers/intercom"

// One request's details (Phelipe 2026-10-17): the panel the Feedback Loop page
// opens from a row — problem, solution, files, history, the customer's
// priority, "email me when it's done", Still not working?, Edit, Delete, Copy
// public link and the comments with files — lifted out of feedback-list so the
// ConsLog chat window can show the very same panel inside itself (Messages › a
// request). Rendered once in the authenticated layout (turbo-permanent, like
// the Instant Feedback panel); the page's list and the chat ask for it by event:
//   feedback-detail:open  {id, title?, focus_comment?, host?}   host "studio" = inside the chat window
//   feedback-detail:back / feedback-detail:leave                the chat window's ‹ and ×
// and it tells the list what changed with the events the list already follows:
//   feedback:request-updated {request, row_html?} · feedback:comment-added · feedback:request-deleted {id, trash_html?}
//   feedback:priority-changed {id, value, from}
export default class extends Controller {
  static targets = ["scrim", "drawer", "dTitle", "dMeta", "dBody", "dLink", "dDelete", "dEdit", "dFoot", "dClose", "dOpen", "confirmModal"]
  static values = { baseUrl: String, listUrl: String, i18n: Object }

  connect() {
    // turbo-permanent: wired once for the whole visit.
    if (this.element.dataset.wired) return
    this.element.dataset.wired = "1"
    this._onOpen = (e) => this.openId(+e.detail?.id, e.detail || {})
    window.addEventListener("feedback-detail:open", this._onOpen)
    this._onBack = () => this.back()
    window.addEventListener("feedback-detail:back", this._onBack)
    this._onLeave = () => { if (this._host) this.close() }
    window.addEventListener("feedback-detail:leave", this._onLeave)
    // The Instant Feedback panel outlives the page: when a recording that was still
    // uploading at Send lands (or the AI writes it up), it tells us; so does the list.
    this._onUpdate = (e) => this._applyUpdate(e.detail?.request)
    window.addEventListener("feedback:request-updated", this._onUpdate)
    // A recording (or words) sent from the panel as an answer lands here; our own
    // comments go out through the same door, marked, so they aren't drawn twice.
    this._onCommentAdded = (e) => { if (e.detail?.from !== "detail") this._commentArrived(e.detail) }
    window.addEventListener("feedback:comment-added", this._onCommentAdded)
    this._onPrio = (e) => this._priorityChanged(e.detail)
    window.addEventListener("feedback:priority-changed", this._onPrio)
    this._onKey = (e) => {
      if (e.key !== "Escape" || !this._openId) return
      // The list's own pop-ups (trash, Notify me) and the reviewers' confirm close first.
      if (document.querySelector('[data-feedback-list-target="trashModal"]:not(.hidden), [data-feedback-list-target="subModal"]:not(.hidden), [data-feedback-review-target="confirm"]:not(.hidden)')) return
      if (this.hasConfirmModalTarget && !this.confirmModalTarget.classList.contains("hidden")) return this.hideConfirm()
      if (this._editing) return this.cancelEdit()
      if (this._reopening) return this.cancelReopen()
      this.close()
    }
    document.addEventListener("keydown", this._onKey)
    this._onResize = () => { if (this._host && this._openId) this._placeHosted() }
    window.addEventListener("resize", this._onResize)
  }

  // ── open / close ─────────────────────────────────────────────────
  async openId(id, opts = {}) {
    if (!id) return
    if (opts.host) this._enterHost(opts.host)
    this._openId = id
    this._openRequest = null
    this._editing = false
    this._reopening = false
    this.dTitleTarget.textContent = opts.title || ""
    this.dMetaTarget.innerHTML = ""
    if (this.hasDLinkTarget) this.dLinkTarget.hidden = true
    if (this.hasDOpenTarget) {
      this.dOpenTarget.hidden = !this._host
      this.dOpenTarget.href = `${this.listUrlValue || this.baseUrlValue}?highlight=${id}`
    }
    this.dBodyTarget.innerHTML = `<div class="text-text-muted">…</div>`
    if (this.hasDFootTarget) this.dFootTarget.hidden = true
    this._paintReviewBar(null)
    this._show()
    track("Feedback Request Opened", { from: this._host ? "chat" : "page" })
    const res = await fetch(`${this.baseUrlValue}/${id}`, { headers: { Accept: "application/json" } }).catch(() => null)
    if (this._openId !== id) return
    if (!res || !res.ok) {
      // Say why instead of spinning: usually the account was switched since
      // this page opened, and the server's line says to refresh.
      const data = res ? await res.json().catch(() => null) : null
      if (this._openId !== id) return
      this.dBodyTarget.innerHTML = `<div class="text-text-muted" data-open-failed>${this._esc(data?.error || this.i18nValue.open_failed || "Couldn't open this request. Refresh the page and try again.")}</div>`
      return
    }
    const { request: r } = await res.json()
    if (this._openId !== id) return
    this._renderDrawer(r)
    // "Open it in ConsLog" in a follow-up email (Phelipe 2026-10-09): straight into the request, answer box ready.
    if (opts.focus_comment) {
      const box = this.dBodyTarget.querySelector("[data-comment-input]")
      box?.scrollIntoView({ block: "center" }); box?.focus()
    }
  }

  _show() {
    // The support chat bubble sits where this panel's bottom row lands
    // (Comment, Copy public link) and covered it (Larry K10-1, 2026-10-20):
    // it steps aside while the panel is open.
    hideIntercom()
    if (this._host) {
      this._hostEvent("shown")
      if (this._placeHosted()) return
      this._leaveHost() // no window to sit in: the side panel, as on the page
    }
    this.scrimTarget.classList.remove("hidden")
    this.drawerTarget.classList.remove("translate-x-full")
  }

  close() {
    this._openId = null
    this._openRequest = null
    this._editing = false
    this._reopening = false
    this.scrimTarget.classList.add("hidden")
    if (this._host) this._leaveHost()
    else this.drawerTarget.classList.add("translate-x-full")
    showIntercom()
  }

  // The chat window's ‹: an inner step first (the edit or reopen form), then the panel itself.
  back() {
    if (!this._openId) return
    if (this._editing) return this.cancelEdit()
    if (this._reopening) return this.cancelReopen()
    this.close()
  }

  // "Open in the Feedback Loop" from inside the chat window: the panel and the
  // window step out of the way, then the page opens on this request.
  openPage(e) {
    e.preventDefault()
    const href = e.currentTarget.href
    this.close()
    window.dispatchEvent(new CustomEvent("feedback-detail:navigate", { detail: { href } }))
    window.Turbo ? window.Turbo.visit(href) : location.assign(href)
  }

  // ── inside the ConsLog chat window (Phelipe 2026-10-17) ──────────
  // The same trick as the Instant Feedback form: the panel stays this
  // controller's own element, drawn over the window's body — same left, width
  // and bottom, from under the window's header down (data-studio-chat-host /
  // data-studio-chat-host-header). The window listens to
  // feedback-detail:host {phase: shown | left} to cover its screens and get them back.
  _enterHost(name) {
    if (this._host) return
    const el = this.drawerTarget
    this._host = { name, className: el.className, style: el.getAttribute("style") || "" }
    el.dataset.hosted = name
    el.className = "fixed bg-white z-[79] flex flex-col"
    if (this.hasDCloseTarget) this.dCloseTarget.hidden = true // the window's ‹ and × do this
  }

  _placeHosted() {
    const host = document.querySelector("[data-studio-chat-host]")
    if (!host || !host.offsetWidth) return false
    const r = host.getBoundingClientRect()
    const head = host.querySelector("[data-studio-chat-host-header]")
    const top = head ? head.getBoundingClientRect().bottom : r.top
    const el = this.drawerTarget
    el.style.display = ""
    el.style.top = `${Math.round(top)}px`
    el.style.left = `${Math.round(r.left)}px`
    el.style.width = `${Math.round(r.width)}px`
    el.style.height = `${Math.round(r.bottom - top)}px`
    el.style.borderRadius = "0 0 16px 16px"
    el.style.transform = "none"
    el.style.transition = "none"
    return true
  }

  _leaveHost() {
    const h = this._host
    if (!h) return
    const el = this.drawerTarget
    el.className = h.className
    if (h.style) el.setAttribute("style", h.style); else el.removeAttribute("style")
    el.classList.add("translate-x-full")
    if (this.hasDCloseTarget) this.dCloseTarget.hidden = false
    if (this.hasDOpenTarget) this.dOpenTarget.hidden = true
    delete el.dataset.hosted
    this._host = null
    window.dispatchEvent(new CustomEvent("feedback-detail:host", { detail: { phase: "left", host: h.name } }))
  }

  _hostEvent(phase) {
    window.dispatchEvent(new CustomEvent("feedback-detail:host", { detail: { phase, host: this._host?.name } }))
  }

  // ── live updates ─────────────────────────────────────────────────
  _applyUpdate(r) {
    if (!r?.id || this._openId !== +r.id) return
    this._renderDrawer(r)
  }

  // The list (and anyone else) hears what changed; row_html is the row the server redrew.
  _broadcast(request, row_html = null) {
    window.dispatchEvent(new CustomEvent("feedback:request-updated", { detail: { request, row_html } }))
  }

  // Approve / N/A at the top while it waits for this reviewer (Phelipe 2026-10-09).
  // The page's feedback-review controller asks "are you sure?" and posts; with no
  // reviewer box on this page (the chat window elsewhere) the bar stays hidden.
  _paintReviewBar(r) {
    const bar = this.element.querySelector("[data-review-bar]")
    if (!bar) return
    const show = !!r && r.review_state === "pending" && !!r.can_review && !!this._reviewer()
    bar.classList.toggle("hidden", !show)
    bar.querySelectorAll("[data-review-approve], [data-review-dismiss]").forEach((btn) => {
      btn.dataset.id = r?.id || ""
      btn.dataset.title = r?.title || ""
      btn.disabled = false
    })
  }

  _reviewer() {
    const el = document.querySelector('[data-controller~="feedback-review"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "feedback-review") : null
  }

  askApprove(e) { e.stopPropagation(); this._reviewer()?.askFor(e.currentTarget, "approve") }
  askDismiss(e) { e.stopPropagation(); this._reviewer()?.askFor(e.currentTarget, "dismiss") }

  // ── the panel's body ─────────────────────────────────────────────
  _renderDrawer(r) {
    // The footer holds Delete (the sender only), Edit (the sender, until someone
    // starts on it — 2026-09-24) and Copy public link (everyone), so it shows for any of them.
    if (this._editing && !r.can_edit) this._editing = false
    if (this._reopening && !r.can_reopen) this._reopening = false
    if (this.hasDFootTarget) { this.dFootTarget.hidden = !(r.can_delete || r.public_url || r.can_edit); this._footFor = r.id }
    if (this.hasDDeleteTarget) this.dDeleteTarget.hidden = !r.can_delete
    if (this.hasDEditTarget) this.dEditTarget.hidden = !r.can_edit || this._editing
    const t = this.i18nValue, esc = this._esc
    // The panel's live updates may not carry the conversation — keep what the
    // drawer already showed, and never throw away a comment being typed.
    if (this._openRequest?.id === r.id) {
      for (const k of ["comments", "watching", "watch_mode", "can_watch", "can_reopen", "closer_look_at", "comment_emails", "cc", "can_cc", "cc_done_email"]) if (r[k] === undefined) r[k] = this._openRequest[k]
    }
    this._openRequest = r
    if (this.hasDLinkTarget) this.dLinkTarget.hidden = !r.public_url
    const draft = this.dBodyTarget.querySelector("[data-comment-input]")?.value || ""
    const reopenNote = this.dBodyTarget.querySelector("[data-reopen-note]")?.value || ""
    // A re-draw (a live update) keeps what's typed in the edit form.
    const form = this.dBodyTarget.querySelector("[data-edit-form]")
    const edit = !this._editing ? null : form
      ? { title: form.querySelector("[data-edit-title]").value, problem: form.querySelector("[data-edit-problem]").value, kind: form.querySelector("[data-edit-kind][data-on=true]")?.dataset.editKind || r.kind }
      : { title: r.title || "", problem: r.problem || "", kind: r.kind }
    const pill = (cls, txt) => `<span class="inline-block text-[12px] font-semibold px-2 py-px rounded-full ${cls}">${esc(txt)}</span>`
    // "Done" plus "(Not yet in the app)" beside it (2026-09-24): the note sits next to the pill, not inside it.
    const statusHtml = st => t.status_notes?.[st] ? `${pill(t.status_pill.done, t.statuses.done)} <span class="text-[12px] text-text-muted whitespace-nowrap">(${esc(t.status_notes[st])})</span>` : pill(t.status_pill[st], t.statuses[st])
    this.dTitleTarget.textContent = r.title
    this._paintReviewBar(r)
    // App / Web (2026-09-24): where the request lives, when it's known.
    const platform = r.platform && t.platforms?.[r.platform] ? `<span>${esc(t.platforms[r.platform])}</span> <span>·</span> ` : ""
    // The reference number (K2-9) leads the line, to quote it like a work order.
    const ref = r.ref ? `<span class="font-semibold text-[13px] text-gray-600 tabular-nums">${esc(r.ref)}</span> ` : ""
    this.dMetaTarget.innerHTML = `${ref}${pill(t.kind_pill[r.kind], t.kinds[r.kind])} ${statusHtml(r.status)} ${platform}<span>${esc(r.area || "")}</span> <span>·</span> <span>${esc(r.origin_label || "")}</span>`
    const files = (r.attachments || []).map(a => this._fileHtml(a)).join("")
    // Still uploading from the panel (the person hit Send before the
    // recording finished): say so where the file is going to appear.
    const pending = (r.pending_uploads || []).map(p => `<div class="flex items-center gap-2.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2.5 text-[13px]"><span class="w-4 h-4 rounded-full border-2 border-blue-300 border-t-blue-800 animate-spin shrink-0"></span><span>${esc(p.role === "recording" ? t.loading_recording : (t.loading_file || "").replace("%{name}", p.name || ""))}</span></div>`).join("")
    const hist = (r.history || []).map(h => `<div><b class="font-semibold text-gray-600">${esc(this._date(h.at))}</b> — ${esc(h.text)}</div>`).join("")
    const prioOpts = `<option value="">${esc(t.none)}</option>` + Object.keys(t.priorities).map(p => `<option value="${p}" ${r.customer_priority === p ? "selected" : ""}>${esc(t.priorities[p])}</option>`).join("")
    // "Email me when this is done" — only while there's something to wait for.
    // Someone the ConsLog team added in pmfit ("Notify people", 2026-09-26)
    // gets every status change instead, and the box says so.
    const updates = r.watch_mode === "updates"
    const watch = r.can_watch ? `<label class="flex items-start gap-2.5 rounded-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
        <input type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand cursor-pointer shrink-0" data-watch-for="${r.id}" data-action="change->feedback-detail#toggleWatch" ${r.watching ? "checked" : ""}>
        <span class="text-gray-400 mt-px shrink-0">${t.bell_icon}</span>
        <span><span class="block font-semibold text-gray-800">${esc(updates ? t.watch_updates_label : t.watch_label)}</span><span class="block text-[13px] text-text-muted">${esc(updates ? t.watch_updates_hint : t.watch_hint)}</span></span>
      </label>` : ""
    // "Email me new comments" (2026-10-28): the sender and whoever commented
    // hear about each new comment; null = not on the thread, so no box.
    const follow = typeof r.comment_emails === "boolean" ? `<label class="flex items-start gap-2.5 rounded-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
        <input type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand cursor-pointer shrink-0" data-comment-emails-for="${r.id}" data-action="change->feedback-detail#toggleCommentEmails" ${r.comment_emails ? "checked" : ""}>
        <span class="text-gray-400 mt-px shrink-0">${t.chat_icon}</span>
        <span><span class="block font-semibold text-gray-800">${esc(t.comment_emails_label)}</span><span class="block text-[13px] text-text-muted">${esc(t.comment_emails_hint)}</span></span>
      </label>` : ""
    // CC teammates (Phelipe 2026-11-02): anyone adds colleagues to this one's emails.
    const cc = r.can_cc ? this._ccHtml(r) : ""
    // Still not working? (Phelipe 2026-09-24): anyone at the company reopens a finished request.
    const reopen = r.can_reopen ? this._reopenHtml() : ""
    const said = (r.comments || []).map(c => this._commentHtml(c)).join("")
    // Files on a comment (Phelipe 2026-09-23): pick, drop or paste; the
    // house chips (blue + spinner while uploading, then grey with ×).
    const talk = `<div class="border-t border-gray-200 pt-3" data-comment-zone
          data-action="dragover->feedback-detail#commentDragOver dragleave->feedback-detail#commentDragLeave drop->feedback-detail#commentDrop">
        <h4 class="m-0 mb-2 flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-gray-500">${t.chat_icon}<span>${esc(t.comments)}</span><span data-comments-total>${(r.comments || []).length || ""}</span></h4>
        <div class="flex flex-col gap-3" data-comments-list>${said || `<p class="m-0 text-[13px] text-text-muted" data-comments-empty>${esc(t.no_comments)}</p>`}</div>
        <textarea rows="3" class="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand resize-y" placeholder="${esc(t.comment_placeholder)}" data-comment-input data-action="keydown->feedback-detail#commentKey paste->feedback-detail#commentPaste"></textarea>
        <div class="flex flex-wrap gap-1.5 mt-2 empty:hidden" data-comment-chips></div>
        ${this._followUpHtml(r)}
        <div class="flex items-center justify-between gap-2 mt-2">
          <div class="flex items-center gap-2 min-w-0 text-[13px] text-text-muted">
            <input type="file" multiple hidden data-comment-file data-action="change->feedback-detail#commentFilesChosen">
            <button type="button" class="inline-flex items-center gap-1 shrink-0 font-semibold text-brand hover:text-brand-dark cursor-pointer" data-action="click->feedback-detail#pickCommentFiles">${t.clip_icon}<span>${esc(t.comment_attach)}</span></button>
            <span class="hidden sm:inline truncate">${esc(t.comment_attach_hint)}</span>
          </div>
          <button type="button" class="shrink-0 px-3.5 py-1.5 rounded-lg bg-brand hover:bg-brand-dark text-white text-[13px] font-semibold cursor-pointer disabled:opacity-60" data-comment-for="${r.id}" data-action="click->feedback-detail#addComment">${esc(t.comment_button)}</button>
        </div>
      </div>`
    const problemHtml = edit ? this._editHtml(edit)
      : `<div class="rounded-lg px-3.5 py-3 bg-amber-50 text-amber-950 leading-relaxed"><h4 class="m-0 mb-1 flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-amber-800"><span>${esc(t.problem)}</span>${this._closerLookMark(r)}</h4>${this._tr(r.problem, r.originals?.problem, "problem")}</div>`
    this.dBodyTarget.innerHTML = `${pending}
      ${problemHtml}
      ${r.observation ? `<div class="rounded-lg px-3.5 py-3 leading-relaxed bg-amber-50 text-amber-950 ring-1 ring-inset ring-amber-200 whitespace-pre-line" data-drawer-observation><h4 class="m-0 mb-1 text-[12px] font-bold uppercase tracking-wide text-amber-800">${esc(t.observation || "Observation")}</h4>${this._tr(r.observation, r.originals?.observation, "observation")}</div>` : ""}
      <div class="rounded-lg px-3.5 py-3 leading-relaxed ${r.solution ? "bg-blue-50 text-blue-950" : "bg-gray-50 text-gray-500 border border-dashed border-gray-300"}"><h4 class="m-0 mb-1 text-[12px] font-bold uppercase tracking-wide ${r.solution ? "text-blue-800" : "text-gray-500"}">${esc(t.solution)}</h4>${r.solution ? this._tr(r.solution, r.originals?.solution, "solution") : esc(t.solution_pending)}</div>
      ${files ? `<div><h4 class="m-0 mb-1.5 text-[12px] font-bold uppercase tracking-wide text-gray-500">${esc(t.what_they_sent)}</h4><div class="flex flex-wrap gap-2.5">${files}</div></div>` : ""}
      <div class="grid grid-cols-2 gap-3">
        <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.your_priority)}</label>
          <select class="w-full h-8 rounded-full border border-gray-300 bg-white text-[12.5px] font-semibold px-2 ${t.prio_cls[r.customer_priority] || ""}" data-drawer-prio data-prio-for="${r.id}" data-action="change->feedback-detail#setPriority">${prioOpts}</select></div>
        <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.status)}</label><div class="pt-1.5">${statusHtml(r.status)}</div></div>
      </div>
      ${watch}
      ${follow}
      ${cc}
      ${reopen}
      ${hist ? `<div class="text-[12.5px] text-text-muted leading-relaxed border-t border-gray-200 pt-2.5">${hist}</div>` : ""}
      ${talk}`
    if (draft) { const box = this.dBodyTarget.querySelector("[data-comment-input]"); if (box) box.value = draft }
    if (reopenNote) { const box = this.dBodyTarget.querySelector("[data-reopen-note]"); if (box) box.value = reopenNote }
    this._renderCommentChips()
  }

  // ── The AI's closer look (Phelipe 2026-10-19) ───────────────────────
  // A few minutes after Send the AI goes over the recording and pictures again
  // (FeedbackDeepReviewJob) and its detail goes into the Problem itself, under
  // the person's own words: no box of its own, no questions back to them (the
  // evening's rule). The server sends closer_look_at to ConsLog's admins only,
  // and they get a small green check next to "The problem"; senders see nothing.
  _closerLookMark(r) {
    if (!r.closer_look_at) return ""
    const label = this._esc((this.i18nValue.deep_review || {}).done || "AI took a closer look")
    return `<span class="inline-flex items-center text-emerald-600" title="${label}" aria-label="${label}" data-closer-look>${this.i18nValue.check_icon || "✓"}</span>`
  }

  // ── In each person's language (Phelipe 2026-10-15) ──────────────────
  // What's saved is English; the server sends this reader's wording plus the
  // other one, and "Show original" swaps them in place.
  _tr(shown, original, field) {
    const esc = this._esc, t = this.i18nValue
    if (!original) return esc(shown || "")
    return `<span data-tr="shown">${esc(shown || "")}</span><span data-tr="original" hidden>${esc(original)}</span>` +
      `<button type="button" class="block mt-1 text-[13px] font-medium text-gray-500 hover:text-gray-700 underline decoration-dotted cursor-pointer whitespace-normal" data-tr-field="${field}" data-action="click->feedback-detail#toggleOriginal">${esc(t.translated || "Translated")} · <span data-tr-label>${esc(t.show_original || "Show original")}</span></button>`
  }

  toggleOriginal(e) {
    e.preventDefault(); e.stopPropagation()
    const btn = e.currentTarget, box = btn.parentElement, t = this.i18nValue
    const shown = box.querySelector(':scope > [data-tr="shown"]'), original = box.querySelector(':scope > [data-tr="original"]')
    if (!shown || !original) return
    const toOriginal = original.hidden
    original.hidden = !toOriginal
    shown.hidden = toOriginal
    btn.querySelector("[data-tr-label]").textContent = toOriginal ? (t.show_translation || "Show translation") : (t.show_original || "Show original")
    if (toOriginal) track("Feedback Original Viewed", { request_id: this._openRequest?.id, field: btn.dataset.trField })
  }

  // ── priority (the customer's own column) ────────────────────────────
  async setPriority(e) {
    const sel = e.currentTarget, id = +sel.dataset.prioFor, value = sel.value
    this._paintPrio(sel, value)
    const res = await fetch(`${this.baseUrlValue}/${id}`, {
      method: "PATCH", headers: this._headers(), body: JSON.stringify({ customer_priority: value })
    }).catch(() => null)
    if (!res || !res.ok) { this._toast(this.i18nValue.priority_cleared, "alert"); return }
    const label = value ? this.i18nValue.priorities[value] : ""
    this._toast(value ? this.i18nValue.priority_saved.replace("%{priority}", label) : this.i18nValue.priority_cleared)
    if (this._openRequest?.id === id) this._openRequest.customer_priority = value
    // The list paints its row's select the same.
    window.dispatchEvent(new CustomEvent("feedback:priority-changed", { detail: { id, value, from: "detail" } }))
  }

  // Changed on the list's row while this request is open here.
  _priorityChanged({ id, value, from } = {}) {
    if (from === "detail" || !id || this._openId !== +id) return
    const d = this.dBodyTarget.querySelector("[data-drawer-prio]")
    if (d && d.value !== value) { d.value = value; this._paintPrio(d, value) }
    if (this._openRequest) this._openRequest.customer_priority = value
  }

  _paintPrio(sel, value) {
    const cls = this.i18nValue.prio_cls || {}
    Object.values(cls).forEach(c => c.split(" ").forEach(k => sel.classList.remove(k)))
    if (cls[value]) cls[value].split(" ").forEach(k => sel.classList.add(k))
  }

  // Copy public link (Phelipe 2026-09-24): the open request's /feedback/p/<token> page, to share anywhere.
  async copyLink() {
    const url = this._openRequest?.public_url, t = this.i18nValue
    if (!url) { this._toast(t.link_failed, "alert"); return }
    let ok = false
    try { await navigator.clipboard.writeText(url); ok = true } catch (_) {
      const ta = document.createElement("textarea")
      ta.value = url; ta.setAttribute("readonly", ""); ta.style.cssText = "position:fixed;left:-9999px;top:0"
      document.body.appendChild(ta); ta.select()
      try { ok = document.execCommand("copy") } catch (_) { ok = false }
      ta.remove()
    }
    this._toast(ok ? t.link_copied : t.link_failed, ok ? undefined : "alert")
  }

  // ── reopen (Phelipe 2026-09-24) ─────────────────────────────────────
  // A finished request that still isn't working: say what's wrong and it goes
  // back to Reopened; pmfit's next sync puts it back on the team's list.
  _reopenHtml() {
    const t = this.i18nValue, o = t.reopen || {}, esc = this._esc
    const box = "rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5"
    if (!this._reopening) {
      return `<div class="${box} flex items-center gap-2.5" data-reopen-box>
          <span class="text-orange-700 shrink-0">${t.reopen_icon || ""}</span>
          <span class="min-w-0 flex-1"><span class="block font-semibold text-orange-900">${esc(o.prompt)}</span><span class="block text-[13px] text-orange-800">${esc(o.hint)}</span></span>
          <button type="button" class="shrink-0 px-3 py-1.5 rounded-lg border border-orange-300 bg-white text-[13px] font-semibold text-orange-800 hover:bg-orange-100 cursor-pointer" data-action="click->feedback-detail#startReopen">${esc(o.button)}</button>
        </div>`
    }
    return `<div class="${box} flex flex-col gap-2" data-reopen-box>
        <label class="flex items-center gap-2 font-semibold text-orange-900"><span class="text-orange-700">${t.reopen_icon || ""}</span>${esc(o.prompt)}</label>
        <textarea rows="3" class="w-full rounded-lg border border-orange-300 bg-white px-3 py-2 text-[13px] focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 resize-y" placeholder="${esc(o.placeholder)}" data-reopen-note data-action="keydown->feedback-detail#reopenKey"></textarea>
        <p class="hidden m-0 text-[13px] text-red-700" data-reopen-err></p>
        <div class="flex items-center justify-end gap-2">
          <button type="button" class="px-3 py-1.5 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-orange-100 cursor-pointer" data-action="click->feedback-detail#cancelReopen">${esc(t.cancel)}</button>
          <button type="button" class="px-3.5 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-[13px] font-semibold cursor-pointer disabled:opacity-60" data-reopen-send data-action="click->feedback-detail#sendReopen">${esc(o.submit)}</button>
        </div>
      </div>`
  }

  startReopen() {
    if (!this._openRequest?.can_reopen) return
    this._reopening = true
    this._renderDrawer(this._openRequest)
    const note = this.dBodyTarget.querySelector("[data-reopen-note]")
    note?.scrollIntoView({ block: "nearest" })
    note?.focus()
  }

  cancelReopen() {
    this._reopening = false
    if (this._openRequest) this._renderDrawer(this._openRequest)
  }

  // Cmd/Ctrl+Enter sends, like the comment box.
  reopenKey(e) { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.sendReopen() } }

  async sendReopen() {
    const t = this.i18nValue, o = t.reopen || {}, r = this._openRequest, box = this.dBodyTarget.querySelector("[data-reopen-box]")
    const input = box?.querySelector("[data-reopen-note]")
    if (!r || !input) return
    const note = input.value.trim(), err = box.querySelector("[data-reopen-err]"), btn = box.querySelector("[data-reopen-send]")
    const fail = (msg) => { err.textContent = msg; err.classList.remove("hidden"); btn.disabled = false }
    if (!note) { input.focus(); return fail(o.empty) }
    btn.disabled = true
    let res = null, data = {}
    try {
      res = await fetch(`${this.baseUrlValue}/${r.id}/reopen`, { method: "POST", headers: this._headers(), body: JSON.stringify({ note }) })
      data = await res.json().catch(() => ({}))
    } catch (_) { res = null }
    // Keep what they wrote and say why (it was reopened meanwhile, or anything else).
    if (!res || !res.ok || !data.request) return fail(data.error || o.failed)
    this._reopening = false
    if (this._openId === r.id) this._renderDrawer(data.request)
    this._broadcast(data.request, data.row_html)
    this._toast(o.toast)
  }

  // ── edit (Phelipe 2026-09-24) ───────────────────────────────────────
  // The person who sent it changes the title, the text and the type until
  // someone starts on it; the server says when (can_edit).
  _editHtml(v) {
    const t = this.i18nValue, esc = this._esc
    const kinds = Object.keys(t.kinds).map(k => `<button type="button" class="rounded-full border border-gray-300 bg-white px-2.5 py-1 text-[13px] font-medium text-gray-600 cursor-pointer data-[on=true]:bg-gray-800 data-[on=true]:text-white data-[on=true]:border-gray-800" data-edit-kind="${k}" data-on="${String(k === v.kind)}" data-action="click->feedback-detail#pickEditKind">${esc(t.kinds[k])}</button>`).join("")
    const field = "w-full rounded-lg border border-gray-300 bg-white text-[13.5px] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
    return `<div class="flex flex-col gap-3 rounded-lg border border-gray-200 px-3.5 py-3" data-edit-form>
        <div><label class="block text-[13px] font-semibold text-gray-600 mb-1">${esc(t.title_label)}</label>
          <input type="text" maxlength="255" class="${field} h-9 px-2.5" value="${esc(v.title)}" data-edit-title data-action="keydown->feedback-detail#editKey"></div>
        <div><label class="block text-[13px] font-semibold text-gray-600 mb-1">${esc(t.describe)}</label>
          <textarea rows="5" class="${field} px-3 py-2 resize-y" data-edit-problem data-action="keydown->feedback-detail#editKey">${esc(v.problem)}</textarea></div>
        <div><label class="block text-[13px] font-semibold text-gray-600 mb-1">${esc(t.what_is_it)}</label><div class="flex flex-wrap gap-1.5">${kinds}</div></div>
        <p class="hidden m-0 text-[13px] text-red-700" data-edit-err></p>
        <div class="flex items-center justify-end gap-2">
          <button type="button" class="px-3 py-1.5 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 cursor-pointer" data-action="click->feedback-detail#cancelEdit">${esc(t.cancel)}</button>
          <button type="button" class="px-3.5 py-1.5 rounded-lg bg-brand hover:bg-brand-dark text-white text-[13px] font-semibold cursor-pointer disabled:opacity-60" data-edit-save data-action="click->feedback-detail#saveEdit">${esc(t.save_changes)}</button>
        </div>
      </div>`
  }

  startEdit() {
    if (!this._openRequest?.can_edit) return
    this._editing = true
    this._renderDrawer(this._openRequest)
    this.dBodyTarget.scrollTop = 0
    this.dBodyTarget.querySelector("[data-edit-title]")?.focus()
  }

  cancelEdit() {
    this._editing = false
    if (this._openRequest) this._renderDrawer(this._openRequest)
  }

  pickEditKind(e) {
    const b = e.currentTarget
    b.parentElement.querySelectorAll("[data-edit-kind]").forEach(x => x.dataset.on = String(x === b))
  }

  // Cmd/Ctrl+Enter saves, like the comment box's Enter.
  editKey(e) { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.saveEdit() } }

  async saveEdit() {
    const t = this.i18nValue, r = this._openRequest, form = this.dBodyTarget.querySelector("[data-edit-form]")
    if (!r || !form) return
    const title = form.querySelector("[data-edit-title]").value.trim(), problem = form.querySelector("[data-edit-problem]").value.trim()
    const kind = form.querySelector("[data-edit-kind][data-on=true]")?.dataset.editKind || r.kind
    const err = form.querySelector("[data-edit-err]"), btn = form.querySelector("[data-edit-save]")
    const fail = (msg) => { err.textContent = msg; err.classList.remove("hidden"); btn.disabled = false }
    if (!title && !problem) return fail(t.edit_empty)
    btn.disabled = true
    let res = null, data = {}
    try {
      res = await fetch(`${this.baseUrlValue}/${r.id}`, { method: "PATCH", headers: this._headers(), body: JSON.stringify({ edit: true, title, problem, kind }) })
      data = await res.json().catch(() => ({}))
    } catch (_) { res = null }
    // Already being worked on (or anything else): say why and keep what they typed.
    if (!res || !res.ok || !data.request) return fail(data.error || t.edit_failed)
    this._editing = false
    if (this._openId === r.id) this._renderDrawer(data.request)
    this._broadcast(data.request, data.row_html)
    this._toast(t.edited_toast)
  }

  // ── files and comments ──────────────────────────────────────────────
  // A recording plays right here (review it); pictures open full size.
  // On a comment (Phelipe 2026-10-09): pictures as large thumbnails, other files as a tile with the type.
  _fileHtml(a, comment = false) {
    const esc = this._esc
    const isImg = a.kind === "image" || a.kind === "screenshot", isVideo = a.kind === "recording" || a.kind === "video"
    // Wrapped for the speed button (video-speed controller, 2026-10-19).
    if (isVideo && a.url) return `<div class="relative basis-full w-full rounded-lg overflow-hidden bg-gray-900" data-controller="video-speed" data-video-speed-title-value="${esc(this.i18nValue.video_speed || "Playback speed")}"><video src="${esc(a.url)}" controls playsinline preload="metadata" class="block w-full max-h-[240px]"></video></div>`
    const ext = String(a.name || "").split(".").pop().toUpperCase().slice(0, 5)
    const body = isImg && a.url ? `<img src="${esc(a.url)}" alt="" class="${comment ? "w-[150px] h-[104px]" : "w-[132px] h-[84px]"} object-cover rounded-lg border border-gray-300 bg-white">`
      : comment ? `<span class="w-[150px] h-[104px] rounded-lg border border-gray-200 bg-gray-50 grid place-items-center text-center text-[12px] text-gray-700 p-2"><span><span class="block text-[11px] font-bold text-gray-500 mb-1">${esc(ext)}</span>${esc(String(a.name || "").slice(0, 40))}</span></span>`
      : `<span class="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 text-gray-700 px-2.5 py-1 text-[13px]">${esc(a.name)}</span>`
    return a.url ? `<a href="${esc(a.url)}" target="_blank" rel="noopener" class="no-underline">${body}</a>` : body
  }

  _commentHtml(c) {
    const esc = this._esc, f = this._followUp()
    const files = (c.attachments || []).map(a => this._fileHtml(a, true)).join("")
    // Follow-up questions (Phelipe 2026-10-09): the team's question wears "Question" (its comment, since
    // 2026-10-12, "Comment"); a reviewers-only note is marked and dashed so reviewers see who can read
    // it; an emailed or public reply says so.
    const sender = this._openRequest?.sender_name || ""
    const tag = (cls, txt) => `<span class="inline-block text-[12px] font-semibold px-2 py-px rounded-full ${cls}">${esc(txt)}</span>`
    const tags = [c.question ? tag("bg-amber-100 text-amber-900", f.teamQuestion) : c.kind === "comment" ? tag("bg-sky-100 text-sky-900", f.teamComment || "Comment") : "",
      c.reviewers_only ? tag("bg-purple-50 text-purple-800 ring-1 ring-inset ring-purple-200", "🔒 " + (f.reviewersOnly || "").replace("%{name}", sender)) : "",
      c.source === "email" ? tag("bg-gray-100 text-gray-600", f.viaEmail) : c.source === "public" ? tag("bg-gray-100 text-gray-600", f.viaPublic) : ""].filter(Boolean).join(" ")
    return `<div class="flex items-start gap-2.5" data-comment-id="${c.id}">
      <span class="w-7 h-7 rounded-full ${c.team ? "bg-brand/15 text-brand-dark" : "bg-gray-200 text-gray-600"} text-[13px] font-semibold inline-flex items-center justify-center shrink-0 mt-0.5">${esc(c.initials)}</span>
      <div class="min-w-0 flex-1">
        <div class="text-[13px] flex flex-wrap items-center gap-x-1.5 gap-y-1"><span class="font-semibold text-gray-800">${esc(c.author)}</span><span class="text-gray-400">${esc(this._dateTime(c.at))}</span>${tags}</div>
        ${c.body ? `<div class="text-[13px] text-gray-700 whitespace-pre-wrap break-words mt-0.5 ${c.reviewers_only ? "rounded-lg border border-dashed border-purple-200 bg-purple-50/60 px-2.5 py-1.5" : ""}">${this._tr(c.body, c.original_body, "comment")}</div>` : ""}
        ${files ? `<div class="flex flex-wrap gap-2 mt-2" data-comment-files>${files}</div>` : ""}
      </div>
    </div>`
  }

  // ── follow-up questions (Phelipe 2026-10-09) ────────────────────────
  _followUp() {
    if (!this._fu) { const el = document.querySelector("[data-feedback-follow-up-i18n]"); this._fu = el ? { ...el.dataset } : {} }
    return this._fu
  }

  // Under the comment box: the team's unanswered question (anyone at the company), a Record my
  // screen button, and — for a reviewer while the request waits for review — the box that emails
  // the sender (ticked) or keeps the comment between reviewers (unticked).
  _followUpHtml(r) {
    const esc = this._esc, f = this._followUp(), sender = r.sender_name || ""
    const hint = r.question_pending ? `<p class="m-0 mt-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-[13px]" data-question-hint>${esc(f.questionHint)}</p>` : ""
    const record = `<button type="button" class="inline-flex items-center gap-1.5 mt-2 text-[13px] font-semibold text-gray-700 hover:text-gray-900 cursor-pointer" data-record-for="${r.id}" data-action="click->feedback-detail#recordForComment" title="${esc(f.recordHint)}"><span class="w-2.5 h-2.5 rounded-full bg-red-500 inline-block"></span>${esc(f.recordBtn)}</button>`
    const review = r.review_state === "pending" && r.reviewer && r.sender_id ? `<label class="flex items-start gap-2 mt-2 text-[13px] text-gray-700 cursor-pointer">
        <input type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand cursor-pointer shrink-0" data-email-sender checked>
        <span><span class="block font-semibold">${esc((f.emailSender || "").replace("%{name}", sender))}</span><span class="block text-[12.5px] text-text-muted">${esc((f.emailSenderHint || "").replace(/%\{name\}/g, sender))}</span></span>
      </label>` : ""
    return `${hint}<div class="flex flex-wrap items-center justify-between gap-x-3">${record}</div>${review}`
  }

  // "Record my screen" in the panel: the Instant Feedback panel records and lands it here as an answer.
  recordForComment(e) {
    const id = +e.currentTarget.dataset.recordFor, r = this._openRequest
    window.dispatchEvent(new CustomEvent("instant-feedback:open", { detail: { comment_for: id, title: r?.title || "" } }))
  }

  _commentArrived(d) {
    if (!d?.id || !d.comment) return
    if (this._openRequest?.id === d.id) this._openRequest.comments = [...(this._openRequest.comments || []), d.comment]
    if (this._openId !== d.id) return
    const list = this.dBodyTarget.querySelector("[data-comments-list]")
    list?.querySelector("[data-comments-empty]")?.remove()
    list?.insertAdjacentHTML("beforeend", this._commentHtml(d.comment))
    list?.lastElementChild?.scrollIntoView({ block: "nearest" })
    const total = this.dBodyTarget.querySelector("[data-comments-total]")
    if (total) total.textContent = d.comment_count
    this._answered(d.id, d.question_pending)
  }

  // The hint under the box goes once an answer has landed (the list hides its row's tag itself).
  _answered(id, stillPending) {
    if (stillPending) return
    if (this._openRequest?.id === id) this._openRequest.question_pending = false
    this.dBodyTarget.querySelector("[data-question-hint]")?.remove()
  }

  // ── files on a comment ───────────────────────────────────────────────
  pickCommentFiles() { this.dBodyTarget.querySelector("[data-comment-file]")?.click() }

  commentFilesChosen(e) {
    this._addCommentFiles(e.currentTarget.files)
    e.currentTarget.value = ""
  }

  // A screenshot or a copied file pasted into the box rides along; pasted
  // words still go into the box as usual.
  commentPaste(e) {
    const files = [...(e.clipboardData?.files || [])]
    if (!files.length) return
    e.preventDefault()
    this._addCommentFiles(files)
  }

  // Inline highlight only (house rule: no page-wide drop overlay).
  commentDragOver(e) {
    if (![...(e.dataTransfer?.types || [])].includes("Files")) return
    e.preventDefault()
    e.currentTarget.classList.add("ring-2", "ring-brand", "bg-brand/5", "rounded-lg")
  }

  commentDragLeave(e) {
    if (e.currentTarget.contains(e.relatedTarget)) return
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5", "rounded-lg")
  }

  commentDrop(e) {
    if (!e.dataTransfer?.files?.length) return
    e.preventDefault()
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5", "rounded-lg")
    this._addCommentFiles(e.dataTransfer.files)
  }

  _addCommentFiles(list) {
    this._commentFiles ||= []
    const id = this._openId
    ;[...list].forEach((file, i) => {
      const name = file.name || `pasted-${Date.now()}${i ? `-${i + 1}` : ""}.png`
      const entry = { name, size: file.size, type: file.type || "application/octet-stream", status: "uploading", forId: id }
      entry.promise = this._uploadCommentFile(entry, file)
      this._commentFiles.push(entry)
    })
    this._renderCommentChips()
  }

  // presign → S3 PUT, the same tmp-cache route every drop zone uses.
  async _uploadCommentFile(entry, file) {
    try {
      const pre = await fetch(`${this.baseUrlValue}/presign`, { method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: entry.name, content_type: entry.type, byte_size: entry.size }] }) })
      const data = await pre.json().catch(() => ({}))
      if (!pre.ok || !data.uploads?.[0]) throw new Error(data.error || "")
      const up = data.uploads[0]
      const put = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": up.content_type }, body: file })
      if (!put.ok) throw new Error("")
      entry.cache_name = up.cache_name
      entry.status = "done"
    } catch (e) {
      entry.status = "failed"
      this._toast(e.message || (this.i18nValue.comment_upload_failed || "").replace("%{name}", entry.name), "alert")
    }
    this._renderCommentChips()
    return entry.status === "done"
  }

  _renderCommentChips() {
    const box = this.dBodyTarget.querySelector("[data-comment-chips]")
    if (!box) return
    const t = this.i18nValue, esc = this._esc
    this._commentFiles ||= []
    box.innerHTML = this._commentFiles.map((f, idx) => {
      if (f.forId !== this._openId) return ""
      const up = f.status === "uploading", bad = f.status === "failed"
      const tone = up ? "bg-blue-50 text-blue-800 border-blue-200" : bad ? "bg-red-50 text-red-800 border-red-200" : "bg-gray-100 text-gray-700 border-gray-200"
      return `<span class="inline-flex items-center gap-1.5 max-w-full rounded-full border px-2.5 py-0.5 text-[13px] font-medium ${tone}">` +
        (up ? `<span class="w-3 h-3 rounded-full border-2 border-blue-300 border-t-blue-800 animate-spin"></span>` : t.file_icon) +
        `<span class="truncate max-w-[180px]">${esc(f.name)}</span>` +
        (up ? "" : `<button type="button" class="px-1 leading-none opacity-70 hover:opacity-100 cursor-pointer" data-comment-rm="${idx}" aria-label="${esc(t.comment_remove_file)}">×</button>`) +
        `</span>`
    }).join("")
    box.querySelectorAll("[data-comment-rm]").forEach(b => { b.onclick = () => { this._commentFiles.splice(+b.dataset.commentRm, 1); this._renderCommentChips() } })
  }

  // ── email me when it's done ─────────────────────────────────────────
  async toggleWatch(e) {
    const box = e.currentTarget, id = +box.dataset.watchFor, on = box.checked
    box.disabled = true
    const res = await fetch(`${this.baseUrlValue}/${id}/${on ? "watch" : "unwatch"}`, {
      method: on ? "POST" : "DELETE", headers: this._headers()
    }).catch(() => null)
    box.disabled = false
    if (!res || !res.ok) { box.checked = !on; this._toast(this.i18nValue.watch_failed, "alert"); return }
    const body = await res.json().catch(() => ({}))
    if (this._openRequest?.id === id) {
      this._openRequest.watching = on
      if (body.watch_mode !== undefined) this._openRequest.watch_mode = body.watch_mode
    }
    const updates = body.watch_mode === "updates"
    this._toast(on ? (updates ? this.i18nValue.watch_updates_label : this.i18nValue.watch_on) : this.i18nValue.watch_off)
  }

  // ── email me new comments ───────────────────────────────────────────
  async toggleCommentEmails(e) {
    const box = e.currentTarget, id = +box.dataset.commentEmailsFor, on = box.checked
    box.disabled = true
    const res = await fetch(`${this.baseUrlValue}/${id}/${on ? "follow_comments" : "unfollow_comments"}`, {
      method: on ? "POST" : "DELETE", headers: this._headers()
    }).catch(() => null)
    box.disabled = false
    if (!res || !res.ok) { box.checked = !on; this._toast(this.i18nValue.watch_failed, "alert"); return }
    if (this._openRequest?.id === id) this._openRequest.comment_emails = on
    this._toast(on ? this.i18nValue.comment_emails_on : this.i18nValue.comment_emails_off)
  }

  // ── CC teammates (Phelipe 2026-11-02) ───────────────────────────────
  // Under the two email boxes: who else gets this request's emails (new
  // comments, and the day it's done while it's still open), as chips with ×,
  // and "Add teammate", which opens a name search right there in the box.
  _ccHtml(r) {
    const t = this.i18nValue, esc = this._esc
    const chips = (r.cc || []).map(p => `<span class="inline-flex items-center gap-1 rounded-full bg-gray-100 pl-2.5 pr-1 py-0.5 text-[12.5px] font-medium text-gray-700">${esc(p.name)}<button type="button" class="w-4 h-4 inline-flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-200 cursor-pointer" data-cc-user="${p.id}" data-action="click->feedback-detail#removeCc" aria-label="${esc(t.comment_remove_file)}">×</button></span>`).join("")
    return `<div class="rounded-lg border border-gray-200 px-3 py-2.5" data-cc-box>
        <div class="flex items-start gap-2.5">
          <span class="text-gray-400 mt-px shrink-0">${t.cc_icon}</span>
          <div class="flex-1 min-w-0">
            <span class="block font-semibold text-gray-800">${esc(t.cc_label)}</span>
            <span class="block text-[13px] text-text-muted">${esc(r.cc_done_email ? t.cc_hint : t.cc_hint_done)}</span>
            <div class="flex flex-wrap items-center gap-1.5 mt-2" data-cc-chips>${chips}
              <button type="button" class="inline-flex items-center gap-1 text-[13px] font-semibold text-brand hover:text-brand-dark cursor-pointer" data-action="click->feedback-detail#openCcPicker" data-cc-add>${t.cc_add_icon}<span>${esc(t.cc_add)}</span></button>
            </div>
            <div class="hidden mt-2" data-cc-picker>
              <input type="text" autocomplete="off" class="w-full px-3 py-1.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" placeholder="${esc(t.cc_search)}" data-cc-search data-action="input->feedback-detail#filterCc keydown->feedback-detail#ccKey">
              <div class="mt-1 max-h-44 overflow-auto border border-gray-200 rounded-lg divide-y divide-gray-100 empty:hidden" data-cc-list></div>
            </div>
          </div>
        </div>
      </div>`
  }

  async openCcPicker() {
    const box = this.dBodyTarget.querySelector("[data-cc-picker]")
    if (!box) return
    box.classList.toggle("hidden")
    if (box.classList.contains("hidden")) return
    const input = box.querySelector("[data-cc-search]")
    input.value = ""
    input.focus()
    if (!this._teammates) {
      const res = await fetch(`${this.baseUrlValue}/teammates`, { headers: { Accept: "application/json" } }).catch(() => null)
      const body = res && res.ok ? await res.json().catch(() => ({})) : {}
      this._teammates = body.people || null
    }
    this.filterCc()
  }

  // Everyone on the company's list but the person picking and whoever's already on it.
  filterCc() {
    const list = this.dBodyTarget.querySelector("[data-cc-list]")
    const input = this.dBodyTarget.querySelector("[data-cc-search]")
    if (!list || !input) return
    const t = this.i18nValue, esc = this._esc
    const q = input.value.trim().toLowerCase()
    const taken = new Set((this._openRequest?.cc || []).map(p => p.id))
    const people = (this._teammates || []).filter(p => !taken.has(p.id) && (!q || p.name.toLowerCase().includes(q))).slice(0, 50)
    list.innerHTML = people.length
      ? people.map(p => `<button type="button" class="block w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer truncate" data-cc-pick="${p.id}" data-action="click->feedback-detail#addCc">${esc(p.name)}</button>`).join("")
      : `<div class="px-3 py-1.5 text-[13px] text-text-muted">${esc(this._teammates ? t.cc_none : t.watch_failed)}</div>`
  }

  // Enter adds the first match; Escape closes the search.
  ccKey(e) {
    if (e.key === "Enter") { e.preventDefault(); this.dBodyTarget.querySelector("[data-cc-pick]")?.click() }
    if (e.key === "Escape") { e.stopPropagation(); this.dBodyTarget.querySelector("[data-cc-picker]")?.classList.add("hidden") }
  }

  async addCc(e) {
    const r = this._openRequest, uid = +e.currentTarget.dataset.ccPick
    if (!r || !uid) return
    e.currentTarget.disabled = true
    const res = await fetch(`${this.baseUrlValue}/${r.id}/add_cc`, { method: "POST", headers: this._headers(), body: JSON.stringify({ user_id: uid }) }).catch(() => null)
    const name = (this._teammates || []).find(p => p.id === uid)?.name || ""
    this._afterCc(r, res, (this.i18nValue.cc_added || "").replace("%{name}", name))
  }

  async removeCc(e) {
    const r = this._openRequest, uid = +e.currentTarget.dataset.ccUser
    if (!r || !uid) return
    e.currentTarget.disabled = true
    const name = (r.cc || []).find(p => p.id === uid)?.name || ""
    const res = await fetch(`${this.baseUrlValue}/${r.id}/remove_cc?user_id=${uid}`, { method: "DELETE", headers: this._headers() }).catch(() => null)
    this._afterCc(r, res, (this.i18nValue.cc_removed || "").replace("%{name}", name))
  }

  // Only the CC box is redrawn, so a comment being typed stays put.
  async _afterCc(r, res, toast) {
    const body = res && res.ok ? await res.json().catch(() => null) : null
    if (!body) { this._toast(this.i18nValue.watch_failed, "alert"); this.filterCc(); return }
    if (this._openRequest?.id !== r.id) return
    this._openRequest.cc = body.cc
    const box = this.dBodyTarget.querySelector("[data-cc-box]")
    if (box) box.outerHTML = this._ccHtml(this._openRequest)
    this._toast(toast)
  }

  // ── comments ────────────────────────────────────────────────────────
  // Cmd/Ctrl+Enter sends, like most comment boxes.
  commentKey(e) {
    if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return
    e.preventDefault()
    this.dBodyTarget.querySelector("[data-comment-for]")?.click()
  }

  async addComment(e) {
    const btn = e.currentTarget, id = +btn.dataset.commentFor
    const input = this.dBodyTarget.querySelector("[data-comment-input]")
    const body = (input?.value || "").trim()
    const pending = (this._commentFiles ||= []).filter(f => f.forId === id && f.status !== "failed")
    if (!body && !pending.length) { this._toast(this.i18nValue.comment_empty, "alert"); input?.focus(); return }
    btn.disabled = true
    const label = btn.textContent
    if (pending.some(f => f.status === "uploading")) {
      btn.textContent = this.i18nValue.comment_waiting
      await Promise.all(pending.map(f => f.promise))
    }
    const files = pending.filter(f => f.status === "done")
      .map(f => ({ cache_name: f.cache_name, filename: f.name, content_type: f.type, byte_size: f.size }))
    if (!body && !files.length) { btn.disabled = false; btn.textContent = label; return }
    // Review chat (2026-10-09): a reviewer's box — ticked emails the sender, unticked keeps it reviewers only.
    const emailBox = this.dBodyTarget.querySelector("[data-email-sender]")
    const extra = emailBox ? { email_sender: emailBox.checked } : {}
    const res = await fetch(`${this.baseUrlValue}/${id}/comments`, {
      method: "POST", headers: this._headers(), body: JSON.stringify({ comment: { body, files }, ...extra })
    }).catch(() => null)
    btn.disabled = false; btn.textContent = label
    if (!res || !res.ok) { this._toast(this.i18nValue.comment_failed, "alert"); return }
    const { comment, comment_count: n, question_pending } = await res.json()
    // The panel may have moved on to another request while this was saving.
    if (input && this._openId === id) input.value = ""
    this._commentFiles = this._commentFiles.filter(f => f.forId !== id)
    this._renderCommentChips()
    const d = { id, comment, comment_count: n, question_pending }
    // Writing a comment puts the writer on the thread's emails (2026-10-28).
    if (this._openRequest?.id === id) this._openRequest.comment_emails = true
    const follow = this.dBodyTarget.querySelector(`[data-comment-emails-for="${id}"]`)
    if (follow) follow.checked = true
    this._commentArrived(d)
    window.dispatchEvent(new CustomEvent("feedback:comment-added", { detail: { ...d, from: "detail" } }))
    this._toast(this.i18nValue.comment_added)
  }

  // ── delete → trash (the sender only); the list moves the row ───────
  askDelete() {
    if (!this._openId || this._footFor !== this._openId) return
    this._deleteId = this._openId
    this.confirmModalTarget.classList.remove("hidden")
  }

  hideConfirm() { if (this.hasConfirmModalTarget) this.confirmModalTarget.classList.add("hidden") }

  stop(e) { e.stopPropagation() }

  async confirmDelete() {
    const id = this._deleteId, t = this.i18nValue
    if (!id) return
    const btn = this.confirmModalTarget.querySelector("[data-confirm-btn]")
    if (btn) btn.disabled = true
    const res = await fetch(`${this.baseUrlValue}/${id}`, { method: "DELETE", headers: this._headers() }).catch(() => null)
    const data = res && res.ok ? await res.json().catch(() => null) : null
    if (btn) btn.disabled = false
    if (!data) { this._toast(t.delete_failed, "alert"); return }
    this.hideConfirm()
    this._deleteId = null
    if (this._openId === id) this.close()
    window.dispatchEvent(new CustomEvent("feedback:request-deleted", { detail: { id, trash_html: data.trash_html } }))
    this._toast(t.deleted_toast)
  }

  // ── small helpers ───────────────────────────────────────────────────
  _date(iso) {
    const d = new Date(iso)
    return isNaN(d) ? "" : d.toLocaleDateString(document.documentElement.lang || undefined, { month: "short", day: "numeric" })
  }

  _dateTime(iso) {
    const d = new Date(iso)
    return isNaN(d) ? "" : d.toLocaleString(document.documentElement.lang || undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  }

  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  _headers() {
    return { "Content-Type": "application/json", Accept: "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "4000")
    el.textContent = message || ""
    document.body.prepend(el)
  }
}
